/**
 * DriversLicenseScanner Component
 *
 * Allows staff to photograph a driver's license (front only, or front + back),
 * sends images to the backend for AI extraction, and pre-fills the
 * demographics form fields with confidence indicators.
 */

import React, { useState, useRef } from 'react';

const CONFIDENCE = {
    HIGH: 80,
    MEDIUM: 40,
    LOW: 1,
};

const MAX_IMAGES = 5;

const DriversLicenseScanner = ({ onFieldsExtracted }) => {
    const [images, setImages] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const fileInputRef = useRef(null);
    const nextId = useRef(0);

    const BASE_URL = process.env.REACT_APP_API_URL;

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const newImages = files.map((file) => ({
            id: nextId.current++,
            file,
            preview: URL.createObjectURL(file),
        }));

        setImages((prev) => {
            const combined = [...prev, ...newImages];
            return combined.slice(0, MAX_IMAGES);
        });

        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRemoveImage = (id) => {
        setImages((prev) => {
            const removed = prev.find((img) => img.id === id);
            if (removed) URL.revokeObjectURL(removed.preview);
            return prev.filter((img) => img.id !== id);
        });
    };

    const handleScan = async () => {
        if (!images.length) return;

        setIsProcessing(true);
        setError(null);
        setResult(null);

        try {
            const formData = new FormData();
            images.forEach((img) => formData.append('dl_images', img.file));

            const response = await fetch(`${BASE_URL}/ocr/scan-drivers-license`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || `Server error: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setResult(data.data);
                if (onFieldsExtracted) {
                    onFieldsExtracted(data.data.fields, data.data.field_details);
                }
            } else {
                throw new Error(data.error || 'Extraction failed');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const getConfidenceColor = (confidence) => {
        if (confidence >= CONFIDENCE.HIGH) return '#22c55e';
        if (confidence >= CONFIDENCE.MEDIUM) return '#eab308';
        if (confidence >= CONFIDENCE.LOW) return '#ef4444';
        return '#9ca3af';
    };

    const reset = () => {
        images.forEach((img) => URL.revokeObjectURL(img.preview));
        setImages([]);
        setResult(null);
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const canAddMore = images.length < MAX_IMAGES && !isProcessing && !result;
    const canScan = images.length > 0 && !isProcessing && !result;

    return (
        <div style={styles.container}>
            <h4 style={styles.heading}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8 }}>
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M7 15h0M2 9.5h20" />
                </svg>
                Scan Driver's License
            </h4>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handleFileSelect}
                disabled={!canAddMore}
                style={styles.fileInput}
                id="dl-upload"
            />

            {!result && (
                <div>
                    {images.length === 0 ? (
                        <label htmlFor="dl-upload" style={styles.uploadLabel}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.5">
                                <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                                <circle cx="12" cy="13" r="4" />
                            </svg>
                            <span style={styles.uploadText}>Take a photo of the driver's license</span>
                            <span style={styles.uploadHint}>Front side required — add back if needed, up to {MAX_IMAGES} images</span>
                        </label>
                    ) : (
                        <div>
                            <div style={styles.thumbnailGrid}>
                                {images.map((img, i) => (
                                    <div key={img.id} style={styles.thumbnailWrapper}>
                                        <img src={img.preview} alt={`License ${i + 1}`} style={styles.thumbnail} />
                                        <button
                                            onClick={() => handleRemoveImage(img.id)}
                                            style={styles.removeButton}
                                            disabled={isProcessing}
                                            title="Remove image"
                                        >
                                            ×
                                        </button>
                                        <span style={styles.thumbnailLabel}>
                                            {i === 0 ? 'Front' : i === 1 ? 'Back' : `Image ${i + 1}`}
                                        </span>
                                    </div>
                                ))}

                                {canAddMore && (
                                    <label htmlFor="dl-upload" style={styles.addMoreTile}>
                                        <span style={styles.addMoreIcon}>+</span>
                                        <span style={styles.addMoreText}>Add photo</span>
                                    </label>
                                )}
                            </div>

                            {canScan && (
                                <button onClick={handleScan} style={styles.scanButton}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                    </svg>
                                    Scan {images.length} image{images.length !== 1 ? 's' : ''}
                                </button>
                            )}

                            {isProcessing && (
                                <div style={styles.processingRow}>
                                    <span style={styles.spinner}></span>
                                    <span style={styles.processingText}>
                                        Extracting information from {images.length} image{images.length !== 1 ? 's' : ''}...
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {error && (
                <div style={styles.error}>
                    <strong>Scan failed:</strong> {error}
                    <button onClick={reset} style={styles.retryButton}>Try Again</button>
                </div>
            )}

            {result && (
                <div>
                    <div style={styles.thumbnailGrid}>
                        {images.map((img, i) => (
                            <div key={img.id} style={styles.thumbnailWrapper}>
                                <img src={img.preview} alt={`License ${i + 1}`} style={styles.thumbnail} />
                                <span style={styles.thumbnailLabel}>
                                    {i === 0 ? 'Front' : i === 1 ? 'Back' : `Image ${i + 1}`}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div style={styles.resultContainer}>
                        <div style={styles.resultHeader}>
                            <div style={styles.resultMeta}>
                                <span style={styles.metaBadge}>
                                    {result.meta.fields_extracted} fields extracted
                                </span>
                                {result.meta.fields_flagged > 0 && (
                                    <span style={{ ...styles.metaBadge, ...styles.flagBadge }}>
                                        {result.meta.fields_flagged} need review
                                    </span>
                                )}
                                <span style={styles.metaTime}>
                                    {(result.meta.processing_time_ms / 1000).toFixed(1)}s
                                </span>
                            </div>
                            <button onClick={reset} style={styles.rescanButton}>Re-scan</button>
                        </div>

                        {result.review_flags.length > 0 && (
                            <div style={styles.flagsContainer}>
                                <p style={styles.flagsTitle}>⚠ Please verify these fields:</p>
                                {result.review_flags.map((flag) => (
                                    <div key={flag.field} style={styles.flagItem}>
                                        <span style={{
                                            ...styles.confidenceDot,
                                            backgroundColor: getConfidenceColor(flag.confidence),
                                        }}></span>
                                        <span style={styles.flagField}>
                                            {flag.field.replace(/_/g, ' ')}
                                        </span>
                                        <span style={styles.flagReason}>{flag.reason}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {result.meta.notes && (
                            <p style={styles.notes}>Note: {result.meta.notes}</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
        backgroundColor: '#f9fafb',
    },
    heading: {
        display: 'flex',
        alignItems: 'center',
        fontSize: 16,
        fontWeight: 600,
        marginBottom: 12,
        color: '#374151',
    },
    fileInput: {
        position: 'absolute',
        width: 0,
        height: 0,
        opacity: 0,
    },
    uploadLabel: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '24px 16px',
        border: '2px dashed #d1d5db',
        borderRadius: 8,
        cursor: 'pointer',
        backgroundColor: '#fff',
    },
    uploadText: {
        fontSize: 14,
        fontWeight: 500,
        color: '#374151',
    },
    uploadHint: {
        fontSize: 12,
        color: '#9ca3af',
    },
    thumbnailGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 10,
    },
    thumbnailWrapper: {
        position: 'relative',
        width: 80,
        flexShrink: 0,
    },
    thumbnail: {
        width: 80,
        height: 56,
        objectFit: 'cover',
        borderRadius: 6,
        border: '1px solid #e5e7eb',
        display: 'block',
    },
    thumbnailLabel: {
        display: 'block',
        fontSize: 10,
        color: '#6b7280',
        textAlign: 'center',
        marginTop: 2,
    },
    removeButton: {
        position: 'absolute',
        top: -6,
        right: -6,
        width: 18,
        height: 18,
        borderRadius: '50%',
        backgroundColor: '#ef4444',
        color: '#fff',
        border: 'none',
        cursor: 'pointer',
        fontSize: 13,
        lineHeight: '16px',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    addMoreTile: {
        width: 80,
        height: 56,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        border: '2px dashed #d1d5db',
        borderRadius: 6,
        cursor: 'pointer',
        backgroundColor: '#fff',
    },
    addMoreIcon: {
        fontSize: 20,
        color: '#9ca3af',
        lineHeight: 1,
    },
    addMoreText: {
        fontSize: 10,
        color: '#9ca3af',
    },
    scanButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        width: '100%',
        padding: '10px 16px',
        fontSize: 14,
        fontWeight: 600,
        backgroundColor: '#3b82f6',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        marginTop: 4,
    },
    processingRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 10,
        padding: '8px 12px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 6,
    },
    processingText: {
        fontSize: 13,
        color: '#1d4ed8',
    },
    spinner: {
        display: 'inline-block',
        width: 16,
        height: 16,
        border: '2px solid #bfdbfe',
        borderTopColor: '#3b82f6',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        flexShrink: 0,
    },
    error: {
        padding: 12,
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 8,
        color: '#991b1b',
        fontSize: 14,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 8,
    },
    retryButton: {
        padding: '4px 12px',
        fontSize: 13,
        backgroundColor: '#fff',
        border: '1px solid #d1d5db',
        borderRadius: 4,
        cursor: 'pointer',
    },
    resultContainer: {
        backgroundColor: '#fff',
        border: '1px solid #d1d5db',
        borderRadius: 8,
        padding: 12,
        marginTop: 8,
    },
    resultHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    resultMeta: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
    metaBadge: {
        padding: '2px 8px',
        fontSize: 12,
        fontWeight: 500,
        backgroundColor: '#dcfce7',
        color: '#166534',
        borderRadius: 12,
    },
    flagBadge: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
    },
    metaTime: {
        fontSize: 12,
        color: '#9ca3af',
    },
    rescanButton: {
        padding: '4px 12px',
        fontSize: 13,
        backgroundColor: '#fff',
        border: '1px solid #d1d5db',
        borderRadius: 4,
        cursor: 'pointer',
    },
    flagsContainer: {
        padding: 10,
        backgroundColor: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 6,
        marginTop: 8,
    },
    flagsTitle: {
        fontSize: 13,
        fontWeight: 600,
        color: '#92400e',
        marginBottom: 6,
    },
    flagItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        padding: '3px 0',
    },
    confidenceDot: {
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
    },
    flagField: {
        fontWeight: 500,
        textTransform: 'capitalize',
        minWidth: 120,
    },
    flagReason: {
        color: '#6b7280',
        fontSize: 12,
    },
    notes: {
        fontSize: 12,
        color: '#6b7280',
        marginTop: 8,
        fontStyle: 'italic',
    },
};

export default DriversLicenseScanner;
